/* Bento product card behaviour: quick add only.
   `snippets/product-card-bento.liquid` renders the card server-side and never
   changes it after paint — no swatches, no in-card variant switching. The one
   hook this file touches is `[data-m-bcard-add][data-variant-id]`: the
   button of a single-variant product, or one of the value buttons of a
   product with a single option (the prints' sizes), each carrying its own
   variant id. Any other product renders that slot as a plain link to the
   product page, so there is nothing for this file to intercept there.

   The listener is delegated on `document`: the Section Rendering API and the
   theme editor replace cards wholesale, and a listener bound to a card would
   die with it. The cart drawer is asked to open through its own element API,
   and the cart refreshes through the shared `LeonartoEvents` bus with the
   payload the theme's own product form emits (`{ cart: response }`) — the
   same shape `<product-form>` uses (`assets/product-info-bento.js`, `onAdded`):
   open the drawer, then emit once. Nothing
   here replaces `#shopify-section-*` markup itself; `m-cart-drawer`
   (`assets/cart-drawer-bento.js` `onCartUpdate`) and `m-cart-count`
   (`assets/theme-components.js` `MCartCount.onCartUpdate`) are both subscribed to
   `cartUpdate` and pick their markup out of `event.cart.sections` on that same
   payload — a manual DOM replace here would just repaint the same regions a
   second time.

   The sections to render come from the `cart:grouped-sections` request, as
   in the product form: the drawer adds its overlay-group section id
   (`sections--…__cart_drawer`), every <m-cart-count> adds `cart-count`.
   A hard-coded `cart-drawer` would render the section with its schema
   defaults under a key the drawer does not read, and cost it a second
   request. */
(function () {
  function sectionsToRender() {
    var sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sections } })
    );
    return sections.filter(function (id, index) { return sections.indexOf(id) === index; }).join(',');
  }

  if (!window.fetch || !window.FormData || !window.Promise) return;

  /* Idempotence. The card snippet may be re-injected by the editor, and a
     second run would attach a second delegated listener — the quick add
     would POST twice. */
  var root = document.documentElement;
  if (root.dataset.mBcardBehaviour === 'true') return;
  root.dataset.mBcardBehaviour = 'true';

  function closest(node, selector) {
    return node && node.closest ? node.closest(selector) : null;
  }

  function settings() {
    return window.LeonartoSettings || {};
  }

  function cartAddUrl() {
    if (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) {
      return window.Theme.routes.cart_add_url;
    }
    return (settings().routes || {}).cart_add_url || '/cart/add.js';
  }

  function setBusy(button, busy) {
    button.classList.toggle('is-loading', busy);
    if (busy) {
      button.setAttribute('aria-busy', 'true');
      button.disabled = true;
    } else {
      button.removeAttribute('aria-busy');
      button.disabled = false;
    }
  }

  function openCartDrawer(trigger) {
    var drawer = document.querySelector('m-cart-drawer');
    if (drawer && typeof drawer.open === 'function') drawer.open(trigger);
  }

  /* `window.LeonartoStrings.quickAddError` comes from `snippets/theme-data.liquid`
     (next to `selectVariant`, the only other string there). The `t` filter HTML-escapes
     the translation (`couldn&#39;t`) and the notification prints its message
     as text, so entities are decoded here first. */
  function decodeEntities(text) {
    var area = document.createElement('textarea');
    area.innerHTML = text;
    return area.value;
  }

  function fallbackErrorMessage() {
    var text = window.LeonartoStrings && window.LeonartoStrings.quickAddError;
    return text ? decodeEntities(text) : 'Something went wrong. Please try again.';
  }

  /* Flags an Error's `message` as shopper-safe: text we put there ourselves,
     from the Cart API's `description` or the fallback above — never a raw
     `fetch`/`JSON.parse` failure, which is a technical string no shopper
     should see. */
  function cartError(message) {
    var error = new Error(message);
    error.isCartError = true;
    return error;
  }

  /* Same notification component `<product-form>` uses (`assets/product-info-bento.js`,
     `notify`; `window.LeonartoTheme.Notification.show` from
     `assets/theme-core.js`). The card has no `.m-product-form-message`
     wrapper of its own, so this follows the form's fallback for that case
     (`sticky: !target`) and appends to `document.body`. */
  function showQuickAddError(message) {
    if (!window.LeonartoTheme || !window.LeonartoTheme.Notification || typeof window.LeonartoTheme.Notification.show !== 'function') {
      return;
    }
    window.LeonartoTheme.Notification.show({
      target: document.body,
      method: 'appendChild',
      type: 'error',
      message: message,
      last: 3000,
      sticky: true,
    });
  }

  function onAddClick(event, button) {
    var variantId = button.getAttribute('data-variant-id');
    if (!variantId || button.disabled) return;
    event.preventDefault();
    setBusy(button, true);

    var formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');
    var sections = sectionsToRender();
    if (sections) {
      formData.append('sections', sections);
      formData.append('sections_url', window.location.pathname);
    }

    window
      .fetch(cartAddUrl(), {
        method: 'POST',
        body: formData,
        credentials: 'same-origin',
        /* Without Accept the endpoint answers with a redirect to /cart (HTML):
           the line is added but nothing here can read the response. */
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      })
      .then(function (response) {
        return response.json();
      })
      .then(function (response) {
        if (!response || response.status || response.errors) {
          var message = (response && (response.description || response.message)) || fallbackErrorMessage();
          throw cartError(message);
        }
        /* Same order as <product-form> (assets/product-info-bento.js): open first, then
           emit. No `#shopify-section-*` replace here — see the file header. */
        openCartDrawer(button);
        if (window.LeonartoEvents && window.LeonartoTheme && window.LeonartoTheme.pubSubEvents) {
          window.LeonartoEvents.emit(window.LeonartoTheme.pubSubEvents.cartUpdate, {
            cart: response,
            productVariantId: variantId,
          });
        }
      })
      .catch(function (error) {
        console.error('[product-card-bento] quick add failed:', error);
        showQuickAddError(error && error.isCartError ? error.message : fallbackErrorMessage());
      })
      .then(function () {
        setBusy(button, false);
      });
  }

  document.addEventListener('click', function (event) {
    var button = closest(event.target, '[data-m-bcard-add]');
    if (button) onAddClick(event, button);
  });
})();
